import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { API_CONFIG } from '../utils/apiConfig';
import HeaderLanding from '../components/partials/HeaderLanding';

const OrderCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const order = searchParams.get('order');
  const type = searchParams.get('type');
  const trxref = searchParams.get('trxref');
  const reference = searchParams.get('reference');

  const [verifying, setVerifying] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const verifyPayment = useCallback(async () => {
    setVerifying(true);
    setError('');
    try {
      const params = { order, type };
      if (trxref) params.trxref = trxref;
      if (reference) params.reference = reference;

      const response = await api.get(API_CONFIG.ORDERS.ORDER_CALLBACK, { params });
      if (response.data?.success) {
        setResult(response.data);
      } else {
        setError(response.data?.error || 'Payment could not be verified');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to verify payment');
    } finally {
      setVerifying(false);
    }
  }, [order, type, trxref, reference]);

  useEffect(() => {
    if (!order) {
      setError('No order reference found in the payment callback URL.');
      setVerifying(false);
      return;
    }
    verifyPayment();
  }, [order, verifyPayment]);

  const formatCurrency = (amount) => {
    if (amount == null) return '₦0.00';
    try {
      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      return `₦${Number(amount).toFixed(2)}`;
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <HeaderLanding />
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl">❌</span>
          </div>
          <h1 className="text-2xl font-bold mb-3">Payment Verification Failed</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={verifyPayment}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/')}
              className="border border-blue-600 text-blue-600 px-6 py-3 rounded-lg hover:bg-blue-50"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const digitalProducts = result?.digitalProducts || [];
  const isAlreadyPaid = !!result?.alreadyPaid;

  return (
    <div className="min-h-screen bg-white">
      {/* <HeaderLanding /> */}

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm p-8 text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl">✅</span>
          </div>
          <h1 className="text-3xl font-bold mb-2">
            {isAlreadyPaid ? 'Payment Already Confirmed' : 'Payment Successful!'}
          </h1>
          <p className="text-gray-600 mb-6">
            {isAlreadyPaid
              ? `Your payment for order #${order} was previously confirmed.`
              : `Thank you for your order. Your payment of ${formatCurrency(result?.total)} has been confirmed.`}
          </p>

          <div className="max-w-md mx-auto bg-gray-50 rounded-lg p-6 mb-6 text-left space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Order ID</span>
              <span className="font-mono font-semibold">{order}</span>
            </div>
            {result?.total != null && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Amount Paid</span>
                <span className="font-semibold">{formatCurrency(result.total)}</span>
              </div>
            )}
            {result?.reference && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Reference</span>
                <span className="font-mono">{result.reference}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate(`/orders-success/${order}`)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
            >
              View Order Details
            </button>
            {result?.hasPhysicalProducts && (
              <button
                onClick={() => navigate(`/track/${order}`)}
                className="border border-blue-600 text-blue-600 px-6 py-3 rounded-lg hover:bg-blue-50"
              >
                Track Order
              </button>
            )}
            <button
              onClick={() => navigate('/')}
              className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50"
            >
              Continue Shopping
            </button>
          </div>
        </div>

        {digitalProducts.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📥</span>
              </div>
              <h2 className="text-2xl font-bold mb-2">Your Digital Products Are Ready!</h2>
              <p className="text-gray-600">A download PIN has been sent to your email.</p>
            </div>

            <div className="space-y-6">
              {digitalProducts.map((product) => (
                <div key={product.productId} className="border border-blue-200 rounded-xl p-6 flex flex-col md:flex-row gap-6 items-center justify-between">
                  <div className="flex items-center gap-4">
                    {product.productImage && (
                      <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={product.productImage}
                          alt={product.productName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-semibold">{product.productName}</h3>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600 mt-1">
                        {product.fileSize && <span>Size: {product.fileSize}</span>}
                        {product.fileType && <span>Format: {product.fileType.toUpperCase()}</span>}
                        {product.maxDownloads && (
                          <span>Downloads: {product.downloadsUsed || 0}/{product.maxDownloads}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/orders-success/${order}`)}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 whitespace-nowrap"
                  >
                    Download Now
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderCallbackPage;
